import { createSlice } from '@reduxjs/toolkit';
import {
  downloadEntriesInPlant,
  getEntriesInPlantData,
  getEntriesInPlantLastUpdateData,
  getEntryInPlantDetailData,
  getGlobalDlzSummaryUrl,
  getParalyzationSummary,
  getWarehouseDlzDetail
} from './async';
import { EntriesInPlantDataState, SelectedInPlantFilters } from './types';
import EntryInPlantDetail from 'modules/inbound/entries/domain/InPlant/EntriesInPlantDetail';
import EntriesWarehouseDlzDetail from 'modules/inbound/entries/domain/InPlant/EntriesWarehouseDlzDetail';
import { EntriesParalyzation } from 'modules/inbound/entries/domain/InPlant/EntriesParalyzation';
import { getColumnsFromLocalStorage } from 'modules/inbound/shared/utils/getColumnsFromLocalStorage';
import { downloadXlsxFile } from 'modules/shared/services/downloadXlsxFile';
import GetDateFormatedService from 'modules/inbound/shared/domain/GetDateFormatedService';
import { getLocalStorage } from 'modules/shared/utils/getLocalStorage';

export const initialState: EntriesInPlantDataState = {
  entriesInPlant: [],
  paralyzationSummary: EntriesParalyzation.generateEmpty(),
  paralyzationSummaryRequestStatus: '',
  lastUpdate: '-',
  columns: [],
  savedColumns: [],
  entriesInPlantRequestStatus: '',
  pagination: {
    page: 1,
    size: 50,
    pageCount: 0,
    totalCount: 0
  },
  selectedColumn: {
    key: '',
    isAscending: false
  },
  filters: {
    selectedSubHeaderFilters: {
      warehouses: [{ text: 'ST12', key: 'ST12' }],
      lastPositions: []
    },
    search: '',
    vehicleSearch: []
  },
  areFiltersSaved: false,
  selectedEntry: null,
  selectedParalyzationStatus: '',
  selectedPrioritizationListStatus: '',
  entryInPlantDetailRequestStatus: '',
  entryInPlantDetail: EntryInPlantDetail.generateEmpty(),
  warehouseDlzDetail: EntriesWarehouseDlzDetail.generateEmpty(),
  warehouseDlzDetailRequestStatus: '',
  globalDlzSummaryUrl: null,
  globalDlzSummaryUrlRequestStatus: '',
  entriesInPlantFile: null,
  downloadRequestStatus: ''
};

const ENTRIES_IN_PLANT_COLUMNS_VIEW_STATE_KEY = 'EntriesST12InPlantColumnsView';
const ENTRIES_IN_PLANT_FILTERS_STATE_KEY = 'EntriesST12InPlantSavedFilters';

const entriesInPlantSlice = createSlice({
  name: 'entriesST12InPlant',
  initialState,
  reducers: {
    updateCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    updateSelectedColumn: (state, action) => {
      state.selectedColumn = action.payload;
    },
    updateFilterSearch: (state, action) => {
      state.filters.search = action.payload;
    },
    updateSelectedLastPositions: (state, action) => {
      state.filters.selectedSubHeaderFilters.lastPositions = action.payload;
    },
    updateSelectedWarehouses: (state, action) => {
      state.filters.selectedSubHeaderFilters.warehouses = action.payload;
    },
    updateFiltervehicleSearch: (state, action) => {
      state.filters.vehicleSearch = action.payload;
    },
    resetFilters: (state, action) => {
      state.filters.selectedSubHeaderFilters =
        initialState.filters.selectedSubHeaderFilters;
      state.filters.vehicleSearch = [];
      localStorage.removeItem(
        `${action.payload}-${ENTRIES_IN_PLANT_FILTERS_STATE_KEY}`
      );
      state.areFiltersSaved = false;
    },
    saveFilters: (state, action) => {
      localStorage.setItem(
        `${action.payload}-${ENTRIES_IN_PLANT_FILTERS_STATE_KEY}`,
        JSON.stringify(state.filters.selectedSubHeaderFilters)
      );
      state.areFiltersSaved = true;
    },
    loadFilters: (state, action) => {
      const localStoragefilters = getLocalStorage<SelectedInPlantFilters>(
        `${action.payload}-${ENTRIES_IN_PLANT_FILTERS_STATE_KEY}`,
        { ...state.filters.selectedSubHeaderFilters }
      );

      state.filters.selectedSubHeaderFilters = {
        warehouses: localStoragefilters.warehouses || [],
        lastPositions: localStoragefilters.lastPositions || []
      };
      state.areFiltersSaved = Object.keys(localStoragefilters).some(
        (key) => localStoragefilters[key].length
      );
    },
    saveColumnsView: (state, action) => {
      localStorage.setItem(
        `${action.payload}-${ENTRIES_IN_PLANT_COLUMNS_VIEW_STATE_KEY}`,
        JSON.stringify(state.savedColumns)
      );
    },
    loadColumnsView: (state, action) => {
      state.savedColumns = getColumnsFromLocalStorage({
        username: action.payload,
        localStorageKey: ENTRIES_IN_PLANT_COLUMNS_VIEW_STATE_KEY,
        defaultColumns: [...state.columns]
      });
    },
    updateVisibleColumns: (state, action) => {
      state.savedColumns = state.savedColumns.map((item) =>
        item.key === action.payload.key
          ? { ...item, isVisibleCheck: !action.payload.isVisibleCheck }
          : item
      );
    },
    updateSelectedEntry: (state, action) => {
      state.selectedEntry = action.payload;
    },
    updateSelectedParalyzationStatus: (state, action) => {
      state.selectedParalyzationStatus = action.payload;
    },
    updateSelectedPrioritizationListStatus: (state, action) => {
      state.selectedPrioritizationListStatus = action.payload;
    },

    updateColumnsOrder: (state, action) => {
      state.savedColumns = action.payload;
    },
    resetDownloadStatus: (state) => {
      state.downloadRequestStatus = initialState.downloadRequestStatus;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getEntriesInPlantData.fulfilled, (state, action) => {
      state.entriesInPlant = action.payload.entries;
      state.columns = action.payload.columns || [];
      state.entriesInPlantRequestStatus = action.meta.requestStatus;
      state.pagination = action.payload.pagination;
    });
    builder.addCase(getEntriesInPlantData.pending, (state, action) => {
      state.entriesInPlantRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getEntriesInPlantData.rejected, (state, action) => {
      state.entriesInPlantRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(
      getEntriesInPlantLastUpdateData.fulfilled,
      (state, action) => {
        state.lastUpdate = action.payload.lastUpdate;
      }
    );
    builder.addCase(getEntryInPlantDetailData.fulfilled, (state, action) => {
      state.entryInPlantDetail = action.payload;
      state.entryInPlantDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getEntryInPlantDetailData.pending, (state, action) => {
      state.entryInPlantDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getEntryInPlantDetailData.rejected, (state, action) => {
      state.entryInPlantDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getWarehouseDlzDetail.fulfilled, (state, action) => {
      state.warehouseDlzDetail = action.payload;
      state.warehouseDlzDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getWarehouseDlzDetail.pending, (state, action) => {
      state.warehouseDlzDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getWarehouseDlzDetail.rejected, (state, action) => {
      state.warehouseDlzDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getParalyzationSummary.fulfilled, (state, action) => {
      state.paralyzationSummary = action.payload;
      state.paralyzationSummaryRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getParalyzationSummary.pending, (state, action) => {
      state.paralyzationSummaryRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getParalyzationSummary.rejected, (state, action) => {
      state.paralyzationSummaryRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getGlobalDlzSummaryUrl.fulfilled, (state, action) => {
      state.globalDlzSummaryUrl = action.payload;
      state.globalDlzSummaryUrlRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getGlobalDlzSummaryUrl.pending, (state, action) => {
      state.globalDlzSummaryUrlRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getGlobalDlzSummaryUrl.rejected, (state, action) => {
      state.globalDlzSummaryUrlRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(downloadEntriesInPlant.fulfilled, (state, action) => {
      state.entriesInPlantFile = action.payload;
      downloadXlsxFile({
        file: action.payload,
        fileName: `P_Currently_in_plant_${GetDateFormatedService.formatCompleteISODate()}`
      });
      state.downloadRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(downloadEntriesInPlant.pending, (state, action) => {
      state.downloadRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(downloadEntriesInPlant.rejected, (state, action) => {
      state.downloadRequestStatus = action.meta.requestStatus;
    });
  }
});

//selectors
export const getEntriesInPlant = (state) =>
  state.entriesST12InPlantState.entriesInPlant;
export const getEntriesParalyzationSummary = (state) =>
  state.entriesST12InPlantState.paralyzationSummary;
export const getEntriesInPlantLastUpdate = (state) =>
  state.entriesST12InPlantState.lastUpdate;
export const getColumns = (state) => state.entriesST12InPlantState.savedColumns;
export const getPageInfo = (state) => state.entriesST12InPlantState.pagination;

export const getEntriesInPlantIsPending = (state) =>
  state.entriesST12InPlantState.entriesInPlantRequestStatus === 'pending';
export const getEntriesInPlantHasError = (state) =>
  state.entriesST12InPlantState.entriesInPlantRequestStatus === 'rejected';
export const getIsParalyzationPending = (state) =>
  state.entriesST12InPlantState.paralyzationSummaryRequestStatus === 'pending';
export const getHasParalyzationError = (state) =>
  state.entriesST12InPlantState.paralyzationSummaryRequestStatus === 'rejected';

export const getSelectedColumn = (state) =>
  state.entriesST12InPlantState.selectedColumn;
export const getFilterSearch = (state) =>
  state.entriesST12InPlantState.filters.search;
export const getSelectedEntryInPlant = (state) =>
  state.entriesST12InPlantState.selectedEntry;
export const getSelectedParalyzationStatus = (state) =>
  state.entriesST12InPlantState.selectedParalyzationStatus;

export const getSelectedPrioritizationListStatus = (state) =>
  state.entriesST12InPlantState.selectedPrioritizationListStatus;
export const getSelectedFilters = (state) =>
  state.entriesST12InPlantState.filters.selectedSubHeaderFilters;
export const getIsFilterSaved = (state) =>
  state.entriesST12InPlantState.areFiltersSaved;
export const getHasFiltersSelected = (state) =>
  Object.keys(
    state.entriesST12InPlantState.filters.selectedSubHeaderFilters
  ).some(
    (key) =>
      state.entriesST12InPlantState.filters.selectedSubHeaderFilters[key].length
  );

export const getEntryInPlantDetailIsPending = (state) =>
  state.entriesST12InPlantState.entryInPlantDetailRequestStatus === 'pending';
export const getEntryInPlantDetailHasError = (state) =>
  state.entriesST12InPlantState.entryInPlantDetailRequestStatus === 'rejected';
export const getWarehouseDlzIsPending = (state) =>
  state.entriesST12InPlantState.warehouseDlzDetailRequestStatus === 'pending';
export const getWarehouseDlzHasError = (state) =>
  state.entriesST12InPlantState.warehouseDlzDetailRequestStatus === 'rejected';
export const getEntryInPlantDetails = (state) =>
  state.entriesST12InPlantState.entryInPlantDetail;
export const getWarehouseDlzTimeline = (state) =>
  state.entriesST12InPlantState.warehouseDlzDetail;

export const getEntriesInPlantDlzUrl = (state) =>
  state.entriesST12InPlantState.globalDlzSummaryUrl;
export const getGlobalDlzSummaryIsPending = (state) =>
  state.entriesST12InPlantState.globalDlzSummaryUrlRequestStatus === 'pending';
export const getGlobalDlzSummaryHasError = (state) =>
  state.entriesST12InPlantState.globalDlzSummaryUrlRequestStatus === 'rejected';
export const getDownloadEntriesInPlantIsPending = (state) =>
  state.entriesST12InPlantState.downloadRequestStatus === 'pending';
export const getDownloadEntriesInPlantSuccessful = (state) =>
  state.entriesST12InPlantState.downloadRequestStatus === 'fulfilled' &&
  state.entriesST12InPlantState.entriesInPlantFile !== null;
export const getDownloadEntriesInPlantError = (state) =>
  state.entriesST12InPlantState.downloadRequestStatus === 'rejected';
export const getFiltervehicleSearch = (state) =>
  state.entriesST12InPlantState.filters.vehicleSearch;
export const {
  updateCurrentPage,
  updateSelectedColumn,
  updateFilterSearch,
  updateSelectedEntry,
  updateVisibleColumns,
  saveColumnsView,
  loadColumnsView,
  updateColumnsOrder,
  updateSelectedParalyzationStatus,
  updateSelectedPrioritizationListStatus,
  updateSelectedLastPositions,
  updateSelectedWarehouses,
  resetFilters,
  saveFilters,
  loadFilters,
  resetDownloadStatus,
  updateFiltervehicleSearch
} = entriesInPlantSlice.actions;
export default entriesInPlantSlice.reducer;
